import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '@/lib/api'
import { Alert, Muted } from '@/components/ui'
import { fetchCargoPrefillVaga } from '@/features/rh/api'
import {
  createVaga,
  fetchCargosOpts,
  fetchDepartamentosOpts,
  fetchEmpresasGrupo,
} from '../api'

type Props = {
  onClose: () => void
}

export function NovaVagaForm({ onClose }: Props) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [cargoLoading, setCargoLoading] = useState(false)
  const [form, setForm] = useState({
    titulo: '',
    cargo: '',
    departamento: '',
    empresa_grupo: '',
    modalidade: 'presencial',
    tipo_contrato: 'clt',
    prioridade: 'media',
    nivel_senioridade: '',
    cidade: '',
    uf: '',
    descricao: '',
    requisitos: '',
    beneficios: '',
    salario_min: '',
    salario_max: '',
    vagas_count: '1',
  })

  const catalogs = useQuery({
    queryKey: ['ats', 'catalogs'],
    queryFn: async () => {
      const [empresas, cargos, departamentos] = await Promise.all([
        fetchEmpresasGrupo(),
        fetchCargosOpts(),
        fetchDepartamentosOpts(),
      ])
      return { empresas, cargos, departamentos }
    },
  })

  const mut = useMutation({
    mutationFn: createVaga,
    onSuccess: async (vaga) => {
      await qc.invalidateQueries({ queryKey: ['ats', 'vagas'] })
      await qc.invalidateQueries({ queryKey: ['ats', 'dashboard'] })
      onClose()
      if (vaga?.id) navigate(`/app/recrutamento/${vaga.id}`)
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Falha ao criar vaga.')
    },
  })

  async function onSelectCargo(id: string) {
    if (!id) {
      setForm((f) => ({ ...f, cargo: '' }))
      setInfo('')
      return
    }
    setError('')
    setInfo('')
    setCargoLoading(true)
    try {
      const snap = await fetchCargoPrefillVaga(id)
      setForm((f) => ({
        ...f,
        cargo: id,
        titulo: snap.titulo || f.titulo,
        departamento: snap.departamento_id || f.departamento,
        descricao: snap.descricao || '',
        requisitos: snap.requisitos || '',
        beneficios: snap.beneficios || '',
        nivel_senioridade: snap.nivel || f.nivel_senioridade,
        salario_min: snap.salario_min != null ? String(snap.salario_min) : '',
        salario_max: snap.salario_max != null ? String(snap.salario_max) : '',
      }))
      if (snap.descritivo_de_cargo_nome) {
        setInfo(
          `Descritivo copiado de “${snap.descritivo_de_cargo_nome}” (mesmo nome). Só esta vaga é alterada.`,
        )
      } else if (!snap.tem_descritivo) {
        setInfo(
          'Este cargo não tem descritivo cadastrado (objetivo/requisitos). Preencha manualmente ou edite o cargo em Cargos.',
        )
      } else {
        setInfo('Descritivo, requisitos e faixa copiados do cargo. Edições ficam só nesta vaga.')
      }
    } catch (err) {
      const opt = catalogs.data?.cargos.find((c) => c.id === id)
      setForm((f) => ({
        ...f,
        cargo: id,
        titulo: opt?.nome || f.titulo,
        departamento: opt?.departamento_id || f.departamento,
      }))
      setError(
        err instanceof ApiError
          ? err.message
          : 'Não foi possível carregar o descritivo do cargo.',
      )
    } finally {
      setCargoLoading(false)
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (!form.titulo.trim()) {
      setError('Informe o título da vaga.')
      return
    }
    if (!form.empresa_grupo) {
      setError('Selecione a empresa (marca) da vaga — ela aparece no portal Carreiras.')
      return
    }
    const salMin = form.salario_min.trim() ? Number(form.salario_min) : null
    const salMax = form.salario_max.trim() ? Number(form.salario_max) : null
    mut.mutate({
      titulo: form.titulo.trim(),
      cargo: form.cargo || null,
      departamento: form.departamento || null,
      empresa_grupo: form.empresa_grupo,
      modalidade: form.modalidade,
      tipo_contrato: form.tipo_contrato,
      prioridade: form.prioridade,
      nivel_senioridade: form.nivel_senioridade || undefined,
      cidade: form.cidade.trim(),
      uf: form.uf.trim().toUpperCase().slice(0, 2),
      descricao: form.descricao.trim(),
      requisitos: form.requisitos.trim(),
      beneficios: form.beneficios.trim(),
      salario_min: salMin != null && !Number.isNaN(salMin) ? salMin : null,
      salario_max: salMax != null && !Number.isNaN(salMax) ? salMax : null,
      vagas_count: Math.max(1, Number(form.vagas_count) || 1),
      status: 'rascunho',
    })
  }

  return (
    <form className="card card-body hi-stack" onSubmit={onSubmit}>
      <h4 className="card-title">Nova vaga</h4>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {info ? <Alert tone="info">{info}</Alert> : null}
      <Muted>
        Ao escolher um cargo, descrição, requisitos, benefícios e faixa são copiados para esta
        vaga. Alterações aqui não mudam o cadastro do cargo.
      </Muted>
      <div className="hi-form-grid">
        <label className="hi-span-all">
          Título *
          <input
            required
            value={form.titulo}
            onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
          />
        </label>
        <label>
          Cargo existente (ativos com colaboradores)
          <select
            value={form.cargo}
            disabled={cargoLoading || mut.isPending}
            onChange={(e) => void onSelectCargo(e.target.value)}
          >
            <option value="">— cargo novo (use o título) —</option>
            {(catalogs.data?.cargos || []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
                {c.headcount != null ? ` (${c.headcount})` : ''}
              </option>
            ))}
          </select>
          {cargoLoading ? <Muted>Carregando descritivo do cargo…</Muted> : null}
        </label>
        <label>
          Departamento
          <select
            value={form.departamento}
            onChange={(e) => setForm((f) => ({ ...f, departamento: e.target.value }))}
          >
            <option value="">—</option>
            {(catalogs.data?.departamentos || []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.nome}
              </option>
            ))}
          </select>
        </label>
        <label>
          Empresa (Carreiras)
          <select
            required
            value={form.empresa_grupo}
            onChange={(e) => setForm((f) => ({ ...f, empresa_grupo: e.target.value }))}
          >
            <option value="">Selecione a marca…</option>
            {(catalogs.data?.empresas || []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.nome}
              </option>
            ))}
          </select>
        </label>
        <label>
          Modalidade
          <select
            value={form.modalidade}
            onChange={(e) => setForm((f) => ({ ...f, modalidade: e.target.value }))}
          >
            <option value="presencial">Presencial</option>
            <option value="hibrido">Híbrido</option>
            <option value="remoto">Remoto</option>
          </select>
        </label>
        <label>
          Contrato
          <select
            value={form.tipo_contrato}
            onChange={(e) => setForm((f) => ({ ...f, tipo_contrato: e.target.value }))}
          >
            <option value="clt">CLT</option>
            <option value="pj">PJ</option>
            <option value="estagio">Estágio</option>
            <option value="temporario">Temporário</option>
            <option value="aprendiz">Aprendiz</option>
          </select>
        </label>
        <label>
          Prioridade
          <select
            value={form.prioridade}
            onChange={(e) => setForm((f) => ({ ...f, prioridade: e.target.value }))}
          >
            <option value="baixa">Baixa</option>
            <option value="media">Média</option>
            <option value="alta">Alta</option>
            <option value="urgente">Urgente</option>
          </select>
        </label>
        <label>
          Quantidade
          <input
            type="number"
            min={1}
            value={form.vagas_count}
            onChange={(e) => setForm((f) => ({ ...f, vagas_count: e.target.value }))}
          />
        </label>
        <label>
          Salário mín.
          <input
            value={form.salario_min}
            onChange={(e) => setForm((f) => ({ ...f, salario_min: e.target.value }))}
            placeholder="Opcional"
          />
        </label>
        <label>
          Salário máx.
          <input
            value={form.salario_max}
            onChange={(e) => setForm((f) => ({ ...f, salario_max: e.target.value }))}
            placeholder="Opcional"
          />
        </label>
        <label>
          Cidade
          <input
            value={form.cidade}
            onChange={(e) => setForm((f) => ({ ...f, cidade: e.target.value }))}
          />
        </label>
        <label>
          UF
          <input
            maxLength={2}
            value={form.uf}
            onChange={(e) => setForm((f) => ({ ...f, uf: e.target.value }))}
          />
        </label>
        <label className="hi-span-all">
          Descrição
          <textarea
            rows={5}
            value={form.descricao}
            onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
          />
        </label>
        <label className="hi-span-all">
          Requisitos
          <textarea
            rows={5}
            value={form.requisitos}
            onChange={(e) => setForm((f) => ({ ...f, requisitos: e.target.value }))}
          />
        </label>
        <label className="hi-span-all">
          Benefícios
          <textarea
            rows={3}
            value={form.beneficios}
            onChange={(e) => setForm((f) => ({ ...f, beneficios: e.target.value }))}
            placeholder="Diferenciais / KPIs do cargo ou benefícios da vaga"
          />
        </label>
      </div>
      <div className="hi-actions">
        <button type="submit" className="btn btn-primary" disabled={mut.isPending || cargoLoading}>
          {mut.isPending ? 'Criando…' : 'Criar rascunho'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Cancelar
        </button>
      </div>
    </form>
  )
}
